import { cn } from '@/components/ui/cn';
import { ChiefHudPaneel } from './ChiefHudPaneel';
import type { PaneelFormaat, PaneelPositie } from './ChiefHudPaneel';
import type { HudPaneelData } from '../../services/voiceScreenService';

/** Eén open paneel in de omgeving, met stabiele identiteit voor animaties. */
export interface HudPaneelInstantie {
  id: string;
  data: HudPaneelData;
  /** Weergaveformaat; per spraak te wijzigen via `beheer_scherm`. */
  formaat: PaneelFormaat;
  /** Positie-kolom; nieuw geopende panelen starten rechts. */
  positie: PaneelPositie;
}

/** Maximaal zoveel panelen tegelijk; de oudste schuift eruit. */
export const MAX_HUD_PANELEN = 3;

interface ChiefHudPanelenProps {
  panelen: HudPaneelInstantie[];
  /** Paneel met een korte spraak-focus-highlight (of null). */
  focusId: string | null;
  onSluit: (id: string) => void;
  /** Handmatig vangnet op het concept-paneel: verzend het open mailconcept. */
  onVerzendConcept?: () => void;
  /** Verzenden loopt; de Verzend-knop op het concept-paneel is dan geblokkeerd. */
  verzendBezig?: boolean;
}

const KOLOM_BASIS =
  'pointer-events-none absolute inset-y-16 z-10 flex flex-col justify-center gap-3';

/** De drie positie-kolommen: links, midden (gecentreerde overlay) en rechts. */
const KOLOMMEN: Array<{ positie: PaneelPositie; className: string }> = [
  { positie: 'links', className: cn(KOLOM_BASIS, 'left-3 items-start sm:left-6') },
  { positie: 'midden', className: cn(KOLOM_BASIS, 'left-1/2 z-20 -translate-x-1/2 items-center') },
  { positie: 'rechts', className: cn(KOLOM_BASIS, 'right-3 items-end sm:right-6') },
];

/**
 * De panelen van de Chief-omgeving, gegroepeerd per positie-kolom: links en
 * rechts van de orb (verticaal gecentreerd) en een gecentreerde
 * midden-overlay. De containers vangen geen kliks (pointer-events-none); de
 * kaarten zelf wél. Panelen starten rechts en zijn per spraak te verplaatsen,
 * te schalen en te sluiten via de tool `beheer_scherm`.
 */
export function ChiefHudPanelen({
  panelen,
  focusId,
  onSluit,
  onVerzendConcept,
  verzendBezig = false,
}: ChiefHudPanelenProps) {
  if (panelen.length === 0) return null;
  return (
    <div role="region" aria-label="Geopende schermen">
      {KOLOMMEN.map(({ positie, className }) => {
        const groep = panelen.filter((paneel) => paneel.positie === positie);
        if (groep.length === 0) return null;
        return (
          <div key={positie} className={className}>
            {groep.map((paneel) => (
              <ChiefHudPaneel
                key={paneel.id}
                paneel={paneel.data}
                formaat={paneel.formaat}
                heeftFocus={paneel.id === focusId}
                onSluit={() => onSluit(paneel.id)}
                onVerzendConcept={onVerzendConcept}
                verzendBezig={verzendBezig}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
